// bankUtils.js — Moteur de rapprochement bancaire SDASSP FO
// Scoring pondéré 0-100 · Contrainte NOM stricte · Fingerprint · Classification

// ─── Levenshtein ──────────────────────────────────────────────────────────────
export function levenshtein(a, b) {
  if (!a && !b) return 0;
  if (!a) return (b || '').length;
  if (!b) return (a || '').length;
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
}

// ─── Normalisation ────────────────────────────────────────────────────────────
export const norm = s =>
  (s || '').toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9 ]/g, ' ')
    .trim().replace(/\s+/g, ' ');

const PARASITES_RE = new RegExp(
  '\\b(?:VIRT?\\s+INSTANTANE\\s+DE|VIRT?\\s+DE|VIREMENT\\s+RE[CÇ]U\\s+DE|VIREMENT\\s+DE' +
  '|VIR\\s+INST|VIR\\s*RECU|NOT\\s*PROVIDED|NOTPROVIDED' +
  '|IMP\\s+PREL|IMPAYE\\s+PREL|REJET\\s+ORDRE|REJET\\s+PRELEVEMENT|PROVISION\\s+INSUF|COMPTE\\s+BLOQUE' +
  '|MME|MLE|MLLE|MR|MADAME|MONSIEUR|M\\s+OU\\s+MME' +
  '|COTISATION|ADHESION|SYNDICAT|SYNDICAL)\\b', 'g'
);

export function normalizeLabel(raw) {
  return norm(raw)
    .replace(PARASITES_RE, '')
    .replace(/\s*\/.*$/, '')
    .replace(/\b(?:DE|DU|LA|LE|LES)\b/g, '')
    .replace(/\s+/g, ' ').trim();
}

// ─── Fingerprint anti-doublon ─────────────────────────────────────────────────
// Format : "DD/MM|9.00|credit|NOMBRUTTRONQUE40"
export function computeFingerprint(date, amount, direction, rawLabel) {
  const normLbl = norm(rawLabel).replace(/\s+/g, '').slice(0, 40);
  return `${date}|${Number(amount).toFixed(2)}|${direction}|${normLbl}`;
}

// ─── Classification des transactions ─────────────────────────────────────────
const R_IMPAYE  = /\b(?:IMPAYE\s*PREL|IMP\s+PREL|REJET\s+ORDRE\s+TIRE|REJET\s+PRELEVEMENT|PROVISION\s+INSUF)/i;
const R_EXCLU   = /\b(?:PRLV\b|PRELEVEMENT\s+EMIS|RETRAIT|VIR\s*EMIS|VIR\s+FAV\b|FRAIS\s+IMPAYE\b|BFCNET|ABO\s+BFC|FRAIS\s+TENUE|FACTUR\s+CONNEXION|COMM\s*PREL|COMMPREL|EXT\s+FRAIS|ACHAT\s+CB|VIR(?:EMENT)?\s+MULTIPLE|REMISE\s+CHEQUE|COTIS\s+CARTE|EBICS|ABO\s+BFC)\b/i;
const R_VIRT    = /\b(?:VIRT?\s+INSTANTANE\s+DE\b|VIRT?\s+DE\b|VIREMENT\s*RE[CÇ]U|VIR\s+INST|VIR\s*RECU\b)/i;

export function classifyTransaction(line) {
  if (R_EXCLU.test(line))  return 'excluded';
  if (R_IMPAYE.test(line)) return 'impaye';
  if (R_VIRT.test(line))   return 'virement';
  return 'autre';
}

// ─── Extraction des candidats nom/prénom depuis le libellé BFC ────────────────
// Format BFC OI habituel : "NOM PRENOM" (majuscules)
export function extractNameCandidates(nomBrut) {
  const clean = normalizeLabel(nomBrut);
  if (!clean || clean.length < 2) return [];

  const candidates = [];

  // Compte joint : "NOM PRENOM1 OU PRENOM2" → deux candidats héritant du NOM
  const ouMatch = clean.match(/^(.+?)\s+OU\s+(.+)$/);
  if (ouMatch) {
    const tokens1 = ouMatch[1].trim().split(' ');
    const nom1    = tokens1[0];
    candidates.push({ nom: nom1, prenom: tokens1.slice(1).join(' ') });
    const part2 = ouMatch[2].trim();
    if (part2.includes(' ')) {
      const t2 = part2.split(' ');
      candidates.push({ nom: t2[0], prenom: t2.slice(1).join(' ') });
    } else {
      candidates.push({ nom: nom1, prenom: part2 });
    }
  } else {
    const tokens = clean.split(' ');
    candidates.push({ nom: tokens[0], prenom: tokens.slice(1).join(' ') });
    // BFC peut mettre PRENOM NOM (ex: "MR ALEXANDRE PAYET" → après strip "ALEXANDRE PAYET")
    // Ajouter un candidat inversé avec le dernier token comme NOM
    if (tokens.length >= 2) {
      const lastNom = tokens[tokens.length - 1];
      if (lastNom.length >= 2 && lastNom !== tokens[0]) {
        candidates.push({ nom: lastNom, prenom: tokens.slice(0, tokens.length - 1).join(' ') });
      }
    }
  }

  return candidates.filter(c => c.nom && c.nom.length >= 2);
}

// ─── Score d'un adhérent contre un candidat ───────────────────────────────────
// Règle absolue : si les 2 premiers caractères du NOM diffèrent → score = 0
// (bloque DAGARD→BENARD, DARID→NARIA, HOAREAU→BAREGE, PAYET→CADET, etc.)
function scoreOne(candidate, adh, montant) {
  const cNom       = candidate.nom;
  const cPre       = candidate.prenom || '';
  const adhNomFull = norm(adh.nom    || '');
  const adhNomFst  = adhNomFull.split(' ')[0];   // 1er mot du nom adhérent
  const adhPreFull = norm(adh.prenom || '');

  if (!adhNomFst || cNom.length < 2) return { score: 0, reasons: [] };

  // ── GATE préfixe 2 caractères ──────────────────────────────────────────────
  if (cNom.slice(0, 2) !== adhNomFst.slice(0, 2)) return { score: 0, reasons: [] };

  const reasons = [];
  let score = 0;

  // ── Scoring NOM (0-50) ────────────────────────────────────────────────────
  const levNom = levenshtein(cNom, adhNomFull);
  if      (levNom === 0) { score += 50; reasons.push('Nom exact'); }
  else if (levNom === 1) { score += 42; reasons.push('Nom ≈'); }
  else if (levNom === 2) { score += 30; reasons.push('Nom proche'); }
  else if (levNom <= 5)  { score += 12; reasons.push('Nom partiel'); }
  else                   { score +=  3; }

  // ── Scoring PRÉNOM (0-25) ─────────────────────────────────────────────────
  if (cPre && adhPreFull) {
    const levPre = levenshtein(cPre, adhPreFull);
    if      (levPre === 0) { score += 25; reasons.push('Prénom exact'); }
    else if (levPre <= 2)  { score += 18; reasons.push('Prénom ≈'); }
    else if (levPre <= 5)  { score += 10; reasons.push('Prénom partiel'); }
    else {
      // Correspondance token par token (prénom composé type "MARIE SANDRA")
      const cToks = cPre.split(' ');
      const aToks = adhPreFull.split(' ');
      const ok = cToks.some(ct => ct.length >= 3 && aToks.some(at => levenshtein(ct, at) <= 1));
      if (ok) { score += 6; reasons.push('Prénom token'); }
    }
  } else if (!cPre) {
    score += 3; // libellé BFC tronqué sans prénom — pas de pénalité
  }

  // ── Scoring MONTANT (0-20) ────────────────────────────────────────────────
  if (adh.montant && montant != null) {
    const diff = Math.abs(montant - adh.montant);
    if (diff < 0.01)                         { score += 20; reasons.push('Montant exact'); }
    else if (adh.montant > 0 &&
             diff / adh.montant < 0.12)      { score +=  8; reasons.push('Montant ≈'); }
  }

  // ── Bonus nom complet exact (les deux ordres) ─────────────────────────────
  const fullCand  = `${cNom} ${cPre}`.trim();
  const fullAdh   = `${adhNomFull} ${adhPreFull}`.trim();
  const fullAdhR  = `${adhPreFull} ${adhNomFull}`.trim();
  const levFull   = Math.min(
    levenshtein(fullCand, fullAdh),
    levenshtein(fullCand, fullAdhR),
  );
  if (levFull === 0) { score += 5; reasons.push('Nom complet exact'); }

  return { score, reasons };
}

// ─── Recherche du meilleur candidat (API principale) ─────────────────────────
export function findBestMatch(nomBrut, adherents, montant = null) {
  if (!nomBrut || !adherents || !adherents.length)
    return { adherentId: null, score: 0, reasons: [], status: 'unknown' };

  const candidates = extractNameCandidates(nomBrut);
  if (!candidates.length)
    return { adherentId: null, score: 0, reasons: [], status: 'unknown' };

  let bestScore   = 0;
  let bestId      = null;
  let bestReasons = [];

  for (const candidate of candidates) {
    for (const adh of adherents) {
      const { score, reasons } = scoreOne(candidate, adh, montant);
      if (score > bestScore) {
        bestScore   = score;
        bestId      = adh.id;
        bestReasons = reasons;
      }
    }
  }

  // Seuils de décision
  // score >= 75 → recognized (était 'auto')
  // score >= 50 → review     (était 'approx')
  // score <  50 → unknown    (était 'none')
  const status =
    bestScore >= 75 ? 'recognized' :
    bestScore >= 50 ? 'review'     :
                      'unknown';

  return {
    adherentId : bestId,
    score      : bestScore,
    reasons    : bestReasons,
    status,
  };
}
